import React, { useEffect, useState, useMemo, useRef } from "react";
import { Row, Col, Badge, Card } from "react-bootstrap";
import { FiCalendar, FiCheckCircle, FiXCircle, FiSun } from "react-icons/fi";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";

import { getAttendence } from "../../store/slices/attendence";
import { getClassteacher } from "../../store/slices/classteacher";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateKey = (year, month, day) =>
  `${year}-${pad2(month + 1)}-${pad2(day)}`;

const toDateKey = (dateValue) => {
  const d = new Date(dateValue);
  return formatDateKey(d.getFullYear(), d.getMonth(), d.getDate());
};

export default function StudentAttendence() {
  const dispatch = useDispatch();

  const user = JSON.parse(localStorage.getItem("user"));
  const teacherId = user?.data?.teacherId;

  const activeStudent = useSelector(
    (state) => state.activestudent?.student
  );

  const [standardId, setStandardId] = useState("");
  const [sectionId, setSectionId] = useState("");

  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const today = new Date();
  const [viewMonth, setViewMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedDateKey, setSelectedDateKey] = useState(toDateKey(today));

  const location = useLocation();
  const fromAddAttendance = location.state?.fromAddAttendance;

  const calendarRef = useRef(null);
  const todayCellRef = useRef(null);

  const getCurrentStudentId = () => activeStudent?._id || "";

  const fetchClassTeacher = async () => {
    try {
      const res = await dispatch(getClassteacher({ teacherId })).unwrap();
      const classteacherData = Array.isArray(res?.data) ? res.data[0] : res?.data;

      if (!classteacherData) {
        toast.error("Class teacher data not found");
        return;
      }

      setStandardId(classteacherData?.standardId?._id || "");
      setSectionId(classteacherData?.sectionId?._id || "");
    } catch (err) {
      toast.error("Failed to fetch class teacher");
    }
  };

  useEffect(() => {
    if (teacherId) fetchClassTeacher();
  }, [teacherId]);


  const fetchAttendanceForMonth = async (monthDate) => {
    const studentId = getCurrentStudentId();
    if (!studentId) return;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();

    const firstDay = formatDateKey(year, month, 1);
    const lastDayNum = new Date(year, month + 1, 0).getDate();

    const now = new Date();
    const isCurrentMonth =
      year === now.getFullYear() && month === now.getMonth();
    const cappedLastDay = isCurrentMonth
      ? toDateKey(now)
      : formatDateKey(year, month, lastDayNum);

    setIsLoading(true);

    try {
      const res = await dispatch(
        getAttendence({ studentId, fromDate: firstDay, toDate: cappedLastDay })
      ).unwrap();

      setAttendanceData(res?.data?.attendanceData || []);
    } catch (err) {
      toast.error("Failed to fetch attendance");
      setAttendanceData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!activeStudent?._id) return;
    fetchAttendanceForMonth(viewMonth);

  }, [activeStudent?._id, viewMonth]);

  useEffect(() => {
    if (fromAddAttendance) {
      const now = new Date();
      setViewMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      setSelectedDateKey(toDateKey(now));
    }

  }, [fromAddAttendance]);


  const attendanceByDate = useMemo(() => {
    const map = {};

    attendanceData.forEach((item) => {
      const dateKey = toDateKey(item.date);
      const studentEntry =
        item.students?.find(
          (s) => s.studentId?._id === activeStudent?._id
        ) || item.students?.[0];

      if (studentEntry) {
        map[dateKey] = studentEntry.status;
      }
    });

    return map;
  }, [attendanceData, activeStudent?._id]);

  const monthDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const leadingBlanks = new Date(year, month, 1).getDay();

    const days = [];

    for (let i = 0; i < leadingBlanks; i++) {
      days.push(null);
    }

    for (let d = 1; d <= totalDays; d++) {
      const dateKey = formatDateKey(year, month, d);
      const dateObj = new Date(year, month, d);
      const isSunday = dateObj.getDay() === 0;
      const isFuture = dateObj > today;

      let status = "none";
      if (isSunday) {
        status = "holiday";
      } else if (attendanceByDate[dateKey] === 1) {
        status = "present";
      } else if (attendanceByDate[dateKey] === 0) {
        status = "absent";
      } else if (isFuture) {
        status = "upcoming";
      }

      days.push({ day: d, dateKey, status, isSunday, isFuture });
    }

    return days;
  }, [viewMonth, attendanceByDate]);

  const monthStats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let holiday = 0;

    monthDays.forEach((cell) => {
      if (!cell) return;
      if (cell.status === "present") present += 1;
      else if (cell.status === "absent") absent += 1;
      else if (cell.status === "holiday") holiday += 1;
    });

    const marked = present + absent;
    const percentage = marked > 0 ? ((present / marked) * 100).toFixed(1) : "0.0";

    return { present, absent, holiday, percentage };
  }, [monthDays]);

  const selectedCell = useMemo(
    () => monthDays.find((cell) => cell && cell.dateKey === selectedDateKey),
    [monthDays, selectedDateKey]
  );

  const monthLabel = viewMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const isViewingCurrentMonth =
    viewMonth.getFullYear() === today.getFullYear() &&
    viewMonth.getMonth() === today.getMonth();

  const goToPrevMonth = () => {
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    if (isViewingCurrentMonth) return;
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const handleGoToToday = () => {
    const now = new Date();
    setViewMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDateKey(toDateKey(now));

    setTimeout(() => {
      calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      todayCellRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const handleDayClick = (cell) => {
    if (!cell) return;
    setSelectedDateKey(cell.dateKey);
  };

  const statusBadge = (status) => {
    if (status === "present") return <Badge bg="success">Present</Badge>;
    if (status === "absent") return <Badge bg="danger">Absent</Badge>;
    if (status === "holiday") return <Badge bg="warning" text="dark">Holiday</Badge>;
    if (status === "upcoming") return <Badge bg="secondary">Upcoming</Badge>;
    return <Badge bg="light" text="dark">No Record</Badge>;
  };

  const isToday = (dateKey) => dateKey === toDateKey(today);


  const summaryCardStyle = {
    background: "linear-gradient(135deg, #FFFFFF 0%, #F8F4FF 55%, #F2ECFF 100%)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",

    border: "2px solid rgba(132,81,255,0.20)",
    // borderLeft: "6px solid #8451FF",

    borderRadius: "22px",

    boxShadow: `
    0 12px 30px rgba(132,81,255,0.15),
    inset 0 1px 1px rgba(255,255,255,0.9)
  `,

    transition: "all .3s ease",
    overflow: "hidden",
  };

  const summaryBodyStyle = {
    padding: "15px",
  };

  const presentIconStyle = {
    width: "50px",
    height: "50px",
    borderRadius: "20px",

    background:
      "linear-gradient(135deg, rgba(132,81,255,0.18), rgba(168,139,250,0.32))",

    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",

    border: "1px solid rgba(132,81,255,0.22)",

    color: "#7C3AED",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    fontSize: "20px",

    boxShadow: `
      0 10px 24px rgba(132,81,255,.18),
      inset 0 1px 0 rgba(255,255,255,.7)
  `,
  };

  const countStyle = {
    fontSize: "34px",
    fontWeight: "800",
    lineHeight: "1",
    letterSpacing: "-1px",

    background: "linear-gradient(135deg, #8451FF 0%, #A855F7 55%, #C084FC 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",

    textShadow: "0 4px 12px rgba(132,81,255,0.15)",
    marginBottom: "6px",

    transition: "all .3s ease",
  };

  const labelStyle = {
    fontSize: "12 px",
    fontWeight: "600",
    color: "#6B7280",

    letterSpacing: "0.4px",

    display: "flex",
    alignItems: "center",
    gap: "6px",

    opacity: 0.9,
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/student/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>


            <FiChevronRight className="separator" />

            <span className="active">Attendance</span>
          </div>

        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={12} sm={4}>
          <Card style={summaryCardStyle}>
            <Card.Body
              className="d-flex align-items-center gap-3"
              style={summaryBodyStyle}
            >
              <div style={presentIconStyle}>
                <FiCheckCircle />
              </div>

              <div>
                <div style={countStyle}>{monthStats.present}</div>
                <div style={labelStyle}>Present this month</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={4} >
          <Card style={summaryCardStyle}>
            <Card.Body className="d-flex align-items-center gap-3" style={summaryBodyStyle}>
              <div style={presentIconStyle}>
                <FiXCircle />
              </div>
              <div>
                <div style={countStyle}>{monthStats.absent}</div>
                <div style={labelStyle}>Absent this month</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={4}>
          <Card style={summaryCardStyle}>
            <Card.Body className="d-flex align-items-center gap-3" style={summaryBodyStyle}>
              <div style={presentIconStyle}>
                <FiSun />
              </div>
              <div>
                <div style={countStyle}>{monthStats.holiday}</div>
                <div style={labelStyle}>Holidays this month</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Calendar */}
      <Card className="att-cal-card" style={{ border: "none" }} ref={calendarRef}>
        <div className="att-legend mx-3">
          <div className="att-legend-item">
            <span className="att-legend-dot" style={{ background: "#198754" }} />
            Present
          </div>
          <div className="att-legend-item">
            <span className="att-legend-dot" style={{ background: "#dc3545" }} />
            Absent
          </div>
          <div className="att-legend-item">
            <span className="att-legend-dot" style={{ background: "#ffc107" }} />
            Holiday (Sunday)
          </div>
          <div className="att-legend-item">
            <span className="att-legend-dot" style={{ background: "#c9cdd6" }} />
            Upcoming
          </div>
        </div>
        <Card.Body>
          <div className="att-cal-header">
            <div className="att-cal-nav">
              <button
                type="button"
                className="att-cal-nav-btn"
                onClick={goToPrevMonth}
                aria-label="Previous month"
              >
                <IoIosArrowBack />
              </button>

              <span className="att-cal-month-label">{monthLabel}</span>

              <button
                type="button"
                className="att-cal-nav-btn"
                onClick={goToNextMonth}
                disabled={isViewingCurrentMonth}
                aria-label="Next month"
              >
                <IoIosArrowForward />
              </button>
            </div>

            <button type="button" className="att-today-btn" onClick={handleGoToToday}>
              <FiCalendar /> Today
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-4 text-muted">Loading attendance...</div>
          ) : (
            <>
              <div className="att-weekday-row">
                {WEEKDAY_LABELS.map((label) => (
                  <div key={label} className="att-weekday-label">
                    {label}
                  </div>
                ))}
              </div>

              <div className="att-day-grid">
                {monthDays.map((cell, idx) => {
                  if (!cell) {
                    return <div key={`blank-${idx}`} className="att-day-cell att-day-empty" />;
                  }

                  const classes = ["att-day-cell", `att-day-${cell.status}`];
                  if (isToday(cell.dateKey)) classes.push("att-day-today");
                  if (cell.dateKey === selectedDateKey) classes.push("att-day-selected");

                  return (
                    <div
                      key={cell.dateKey}
                      ref={isToday(cell.dateKey) ? todayCellRef : null}
                      className={classes.join(" ")}
                      onClick={() => handleDayClick(cell)}
                    >
                      {cell.day}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {selectedCell && (
        <Card className="att-detail-card my-4" style={{ border: "none" }}>
          <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="att-detail-date">
              {new Date(selectedCell.dateKey).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
            {statusBadge(selectedCell.status)}
          </Card.Body>
        </Card>
      )}

    </>
  );
}